import axios from "axios";
import React, { useEffect, useState, useRef } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { NavLink } from "react-router-dom";
import { toast } from "react-toastify";
import apiKey from "../../../config/apiKey";
import { FaLocationDot } from "react-icons/fa6";
import { useTranslation } from "react-i18next";

const Input = React.forwardRef(({ className = "", ...props }, ref) => (
  <input
    ref={ref}
    className={
      "w-full rounded-xl border border-gray-200 bg-[#F9FBFF] px-3 py-2.5 text-sm outline-none focus:border-[#EE5F2B] focus:ring-2 focus:ring-[#EE5F2B]/20 transition " +
      className
    }
    {...props}
  />
));
Input.displayName = "Input";

const Label = ({ children }) => (
  <label className="block text-sm font-medium text-gray-700 mb-1.5">
    {children}
  </label>
);

const SectionCard = ({ title, children, right }) => (
  <div className="rounded-2xl border border-gray-100 bg-white shadow-sm">
    <div className="flex items-center justify-between px-5 pt-5">
      <h3 className="text-[18px] font-semibold text-gray-900">{title}</h3>
      {right}
    </div>
    <div className="px-5 pb-5 pt-3">{children}</div>
  </div>
);

const ProfileSetting = () => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({});
  const [errorsUser, setErrorsUser] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const teacher = JSON.parse(localStorage.getItem("edo-teacher"));
  const user = {
    id: teacher ? teacher.teacher.id : "",
    email: teacher ? teacher.teacher.email : "",
  };

  const [formData, setFormData] = useState({
    email: user.email || "",
    nic: "",
    country: "",
    city: "",
    state: "",
    country_code: "",
    location: "",
    address_1: "",
    address_2: "",
    zip: "",
    lang_id: "",
  });

  const [languages, setLanguages] = useState([]);
  const [formUser, setFormUser] = useState({ name: "", phone: "" });

  // lock email once (avoid flipping during typing)
  const [emailLocked] = useState(Boolean(user.email));

  // ======= Google Places Autocomplete =======
  const autocompleteRef = useRef(null);

  useEffect(() => {
    if (!window.google || !autocompleteRef.current) return;

    const ac = new window.google.maps.places.Autocomplete(
      autocompleteRef.current,
      {
        componentRestrictions: { country: "pk" },
        fields: ["address_components", "formatted_address"],
      }
    );

    const listener = ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      const comps = place.address_components || [];
      const get = (type) =>
        comps.find((c) => c.types.includes(type))?.long_name || "";

      setFormData((prev) => ({
        ...prev,
        country: get("country"),
        city: get("locality") || "Karachi",
        state: get("administrative_area_level_1"),
        zip: get("postal_code"),
        location: place.formatted_address || prev.location,
      }));
    });

    return () =>
      listener && window.google.maps.event.removeListener(listener);
  }, []);

  // ======= Handlers =======
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
    setErrors((s) => ({ ...s, [name]: "" }));
  };

  const handleChangeUser = (e) => {
    const { name, value } = e.target;
    setFormUser((s) => ({ ...s, [name]: value }));
    setErrorsUser((s) => ({ ...s, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await Promise.all([
        axios.post(`${apiKey}/api/edolutions/teacher/teacher-profile`, formData, {
          headers: { Authorization: `Bearer ${teacher?.token}` },
        }),
        axios.put(
          `${apiKey}/api/edolutions/teacher/updateuser/${user.id}`,
          formUser,
          { headers: { Authorization: `Bearer ${teacher?.token}` } }
        ),
      ]);

      toast.success(t("teacherDash.profileSetting.updateSuccess"), {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
        style: { background: "#2A3A54", color: "#fff" },
      });

      // refresh languages optionally
      const getResponse = await axios.get(
        `${apiKey}/api/edolutions/teacher/language`,
        { headers: { Authorization: `Bearer ${teacher?.token}` } }
      );
      setLanguages(getResponse.data || []);
    } catch (err) {
      if (err.response) {
        if (err.response.data?.errors) setErrorsUser(err.response.data.errors);
        if (err.response.data?.data) setErrors(err.response.data.data);
      } else {
        setErrors({ general: t("teacherDash.otherDetails.unexpectedError") });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ======= Prefetch =======
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    const headers = { Authorization: `Bearer ${teacher?.token}` };

    const run = async () => {
      setLoading(true);
      try {
        const [prof, usr, langs] = await Promise.all([
          axios.get(`${apiKey}/api/edolutions/teacher/teacher-profile`, {
            headers,
          }),
          axios.get(
            `${apiKey}/api/edolutions/teacher/getuser/${user.id}`,
            { headers }
          ),
          axios.get(`${apiKey}/api/edolutions/teacher/language`, {
            headers,
          }),
        ]);

        if (!cancel) {
          if (Array.isArray(prof.data) && prof.data[0]) {
            setFormData((s) => ({ ...s, ...prof.data[0] }));
          }
          if (usr.data) setFormUser(usr.data);
          setLanguages(langs.data || []);
        }
      } catch (e) {
        // keep silent logging
        console.error(e);
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    run();
    return () => {
      cancel = true;
    };
  }, [teacher?.token, user.id]);

  const getLanguageName = (id) => {
    const language = languages.find((lang) => String(lang.id) === String(id));
    return language ? language.language_name : "";
  };
const handleClick = () => {
  // add user_id to redirect URL
  window.location.href = `${apiKey}/google/oauth/redirect?uid=${user.id}`;
};


  return (
    <>
      <div className="flex items-center xl:p-6 sm:p-0 justify-center">
        <div className="w-full max-w-6xl">
          {/* HEADER + TABS */}
          <div className="mb-5">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold tracking-tight text-gray-900">
                {t("teacherDash.common.settings")}
              </h1>
              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className={`inline-flex items-center justify-center rounded-xl px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition
                  ${isLoading ? "opacity-60 cursor-not-allowed" : "hover:opacity-95"}
                  bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]`}
              >
                {isLoading ? (
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                ) : (
                  t("teacherDash.common.save")
                )}
              </button>
            </div>

            {/* Tabs */}
            <div className="mt-4 flex flex-wrap items-center gap-6 border-b">
              <NavLink
                to=".././profile-setting-tech"
                className={({ isActive }) =>
                  "pb-3 -mb-px text-sm font-semibold transition " +
                  (isActive
                    ? "text-[#EE5F2B] border-b-2 border-[#EE5F2B]"
                    : "text-gray-600 hover:text-gray-900")
                }
              >
                {t("teacherDash.settingTabs.profileSettings")}
              </NavLink>
              <NavLink
                to=".././specialization-tech"
                className={({ isActive }) =>
                  "pb-3 -mb-px text-sm font-semibold transition " +
                  (isActive
                    ? "text-transparent bg-clip-text bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] border-b-2 border-[#EE5F2B]"
                    : "text-gray-600 hover:text-gray-900")
                }
              >
                {t("teacherDash.settingTabs.specializationsWp")}
              </NavLink>
              <NavLink
                to=".././other-detail"
                className={({ isActive }) =>
                  "pb-3 -mb-px text-sm font-semibold transition " +
                  (isActive
                    ? "text-transparent bg-clip-text bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] border-b-2 border-[#EE5F2B]"
                    : "text-gray-600 hover:text-gray-900")
                }
              >
                {t("teacherDash.settingTabs.otherDetails")}
              </NavLink>
              <NavLink
                to=".././security"
                className={({ isActive }) =>
                  "pb-3 -mb-px text-sm font-semibold transition " +
                  (isActive
                    ? "text-transparent bg-clip-text bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] border-b-2 border-[#EE5F2B]"
                    : "text-gray-600 hover:text-gray-900")
                }
              >
                {t("teacherDash.settingTabs.privacySecurity")}
              </NavLink>
            </div>
          </div>

          {/* LOADING OVERLAY */}
          {loading ? (
            <div className="w-full py-10 text-center relative">
              <div className="fixed inset-0 flex flex-col items-center justify-center bg-gray-200/30 backdrop-blur-sm z-50">
                <div className="w-12 h-12 border-4 border-t-4 border-gray-200 rounded-full animate-spin border-t-[#EE5F2B]" />
                <p className="mt-4 text-lg font-semibold text-[#EE5F2B]">
                  {t("teacherDash.homeTution.loadingText")}
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* ===== Profile ===== */}
              <SectionCard title={t("teacherDash.profileSetting.profileCardTitle")}>
                <div className="space-y-4">
                  <div>
                    <Label>{t("teacherDash.profileSetting.fullNameLabel")}</Label>
                    <Input
                      placeholder={t("teacherDash.profileSetting.fullNameLabel")}
                      type="text"
                      name="name"
                      value={formUser.name || ""}
                      onChange={handleChangeUser}
                    />
                    {errorsUser.name && (
                      <p className="text-xs text-red-600 mt-1">
                        {errorsUser.name[0]}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>{t("teacherDash.common.email")}</Label>
                      <Input
                        placeholder={t("teacherDash.common.email")}
                        type="text"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        disabled={emailLocked}
                      />
                      {errors.email && (
                        <p className="text-xs text-red-600 mt-1">
                          {t("teacherDash.profileSetting.emailRequiredError")}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label>{t("teacherDash.profileSetting.nicLabel")}</Label>
                      <Input
                        placeholder={t("teacherDash.profileSetting.nicLabel")}
                        type="text"
                        name="nic"
                        value={formData.nic || ""}
                        onChange={handleChange}
                      />
                      {errors.nic && (
                        <p className="text-xs text-red-600 mt-1">
                          {errors.nic[0]}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                  <button onClick={handleClick} className="btn btn-primary">
      {t("teacherDash.profileSetting.connectGoogleCalendar")}
    </button>
              </SectionCard>

              {/* ===== Location ===== */}
              <SectionCard
                title={t("teacherDash.common.location")}
                right={
                  <span className="inline-flex items-center gap-2 text-xs text-gray-500">
                    <FaLocationDot className="text-gray-400" />
                    {t("teacherDash.profileSetting.poweredByGooglePlaces")}
                  </span>
                }
              >
                <div className="space-y-4">
                  <div>
                    <Label>{t("teacherDash.profileSetting.searchLocationLabel")}</Label>
                    <div className="relative">
                      <Input
                        placeholder={t("teacherDash.profileSetting.locationTypingPlaceholder")}
                        type="text"
                        name="location"
                        value={formData.location || ""}
                        onChange={handleChange}
                        ref={autocompleteRef}
                        className="pr-10"
                      />
                      <FaLocationDot className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>
                  </div>
                </div>
              </SectionCard>

              {/* ===== Region & Language ===== */}
              <SectionCard title={t("teacherDash.profileSetting.regionLanguageCardTitle")}>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>{t("teacherDash.profileSetting.countryLabel")}</Label>
                      <Input
                        placeholder={t("teacherDash.profileSetting.countryLabel")}
                        type="text"
                        name="country"
                        value={formData.country || ""}
                        onChange={handleChange}
                      />
                      {errors.country && (
                        <p className="text-xs text-red-600 mt-1">
                          {errors.country[0]}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label>{t("teacherDash.profileSetting.stateProvinceLabel")}</Label>
                      <Input
                        placeholder={t("teacherDash.profileSetting.stateProvinceLabel")}
                        type="text"
                        name="state"
                        value={formData.state || ""}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>{t("teacherDash.profileSetting.address1Label")}</Label>
                      <Input
                        placeholder={t("teacherDash.profileSetting.address1Label")}
                        type="text"
                        name="address_1"
                        value={formData.address_1 || ""}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <Label>{t("teacherDash.profileSetting.address2Label")}</Label>
                      <Input
                        placeholder={t("teacherDash.profileSetting.address2Label")}
                        type="text"
                        name="address_2"
                        value={formData.address_2 || ""}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>{t("teacherDash.profileSetting.cityLabel")}</Label>
                      <Input
                        placeholder={t("teacherDash.profileSetting.cityLabel")}
                        type="text"
                        name="city"
                        value={formData.city || ""}
                        onChange={handleChange}
                      />
                      {errors.city && (
                        <p className="text-xs text-red-600 mt-1">
                          {errors.city}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label>{t("teacherDash.profileSetting.zipLabel")}</Label>
                      <Input
                        placeholder={t("teacherDash.profileSetting.zipPlaceholder")}
                        type="text"
                        name="zip"
                        value={formData.zip || ""}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="w-full md:w-1/2">
                    <Label>{t("teacherDash.profileSetting.languageLabel")}</Label>
                    <select
                      name="lang_id"
                      value={formData.lang_id || ""}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-gray-200 bg-[#F9FBFF] px-3 py-2.5 text-sm outline-none focus:border-[#EE5F2B] focus:ring-2 focus:ring-[#EE5F2B]/20 transition text-gray-700"
                    >
                      <option value="" disabled>
                        {getLanguageName(formData.lang_id) || t("teacherDash.profileSetting.selectLanguageOption")}
                      </option>
                      {languages.map((lang) => (
                        <option value={lang.id} key={lang.id}>
                          {lang.language_name}
                        </option>
                      ))}
                    </select>
                    {errors.lang_id && (
                      <p className="text-xs text-red-600 mt-1">
                        {errors.lang_id}
                      </p>
                    )}
                  </div>
                </div>
              </SectionCard>

              {/* ===== Mobile Number ===== */}
              <SectionCard title={t("teacherDash.profileSetting.mobileNumberCardTitle")}>
                <p className="text-[12px] text-gray-500">
                  {t("teacherDash.profileSetting.mobileVerificationNote")}
                </p>
                <div className="mt-4">
                  <PhoneInput
                    containerStyle={{ position: "relative", width: "100%" }}
                    dropdownStyle={{ top: "auto", bottom: "100%" }}
                    inputStyle={{
                      width: "100%",
                      background: "#F9FBFF",
                      border: "1px solid #E5E7EB",
                      borderRadius: "12px",
                      paddingLeft: "48px",
                      height: "42px",
                      fontSize: "14px",
                    }}
                    buttonStyle={{
                      background: "transparent",
                      border: "none",
                      borderTopLeftRadius: "12px",
                      borderBottomLeftRadius: "12px",
                    }}
                    placeholder={t("teacherDash.common.phone")}
                    country={"pk"}
                    inputProps={{ required: true, name: "phone" }}
                    value={formUser.phone || ""}
                    onChange={(val) =>
                      setFormUser((s) => ({ ...s, phone: val }))
                    }
                  />
                  {errorsUser.phone && (
                    <p className="text-xs text-red-600 mt-1">
                      {errorsUser.phone[0]}
                    </p>
                  )}
                </div>
              </SectionCard>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProfileSetting;
